import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, Shield, AlertTriangle, CheckCircle, User } from 'lucide-react';
import axios from 'axios';
import { useNotifications } from '../../hooks/useNotifications';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, Tabs } from '../../components/UI';

const PasswordReset: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'self' | 'force'>('self');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const notifications = useNotifications();
  const [showPasswords, setShowPasswords] = useState<{
    newPassword: boolean;
    confirmPassword: boolean;
    forcePassword: boolean;
  }>({
    newPassword: false,
    confirmPassword: false,
    forcePassword: false
  });

  // Self reset form
  const [selfForm, setSelfForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Force reset form
  const [forceForm, setForceForm] = useState({
    email: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleSelfInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSelfForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleForceInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForceForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSelfReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/auth/reset-password', {
        newPassword: selfForm.newPassword,
        confirmPassword: selfForm.confirmPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessage({ type: 'success', text: response.data.message });
      notifications.success.passwordChanged();
      setSelfForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Erro ao resetar senha' 
      });
      notifications.error.passwordChange(error.response?.data?.message || 'Erro ao resetar senha');
    } finally {
      setLoading(false);
    }
  };

  const handleForceReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/auth/force-reset-password', {
        email: forceForm.email,
        newPassword: forceForm.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessage({ type: 'success', text: response.data.message });
      notifications.success.passwordChanged();
      setForceForm({ email: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Erro ao resetar senha' 
      });
      notifications.error.passwordChange(error.response?.data?.message || 'Erro ao resetar senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <IconButton action="back" onClick={() => navigate('/admin')} />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{t('admin.passwordReset')}</h1>
                <p className="text-gray-600">Gerencie senhas de usuários do sistema</p>
              </div>
            </div>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
            message.type === 'success' 
              ? 'bg-green-100 border border-green-400 text-green-700' 
              : 'bg-red-100 border border-red-400 text-red-700'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertTriangle className="w-5 h-5" />
            )}
            <span>{message.text}</span>
          </div>
        )}

        {/* Tabs */}
        <Tabs
          className="mb-8"
          value={activeTab}
          onChange={setActiveTab}
          items={[
            { id: 'self', label: 'Minha Senha', icon: Lock },
            { id: 'force', label: 'Reset Forçado', icon: Shield },
          ]}
        />

        {/* Content */}
        <div className="bg-white rounded-lg shadow">
          {activeTab === 'self' ? (
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-6">
                <Lock className="w-6 h-6 text-primary-600" />
                <h2 className="text-xl font-semibold text-gray-900">Alterar Minha Senha</h2>
              </div>

              <form onSubmit={handleSelfReset} className="space-y-6">
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Nova Senha *
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.newPassword ? 'text' : 'password'}
                      id="newPassword"
                      name="newPassword"
                      value={selfForm.newPassword}
                      onChange={handleSelfInputChange}
                      required
                      minLength={8}
                      className="field field--lg pr-12"
                      placeholder="Digite a nova senha"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('newPassword')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.newPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Mínimo de 8 caracteres</p>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmar Nova Senha *
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.confirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={selfForm.confirmPassword}
                      onChange={handleSelfInputChange}
                      required
                      minLength={8}
                      className="field field--lg pr-12"
                      placeholder="Confirme a nova senha"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('confirmPassword')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.confirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="form-actions">
                  <Button variant="secondary"
                    type="button"
                    onClick={() => navigate('/admin')}>
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}>
                    <Lock className="w-4 h-4" />
                    <span>{loading ? 'Alterando...' : 'Alterar Senha'}</span>
                  </Button>
                </div>
              </form>
            </div>
          ) : (
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-6">
                <Shield className="w-6 h-6 text-orange-600" />
                <h2 className="text-xl font-semibold text-gray-900">Reset Forçado de Senha</h2>
              </div>

              <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-orange-800">Atenção</h3>
                    <p className="text-sm text-orange-700 mt-1">
                      Esta funcionalidade permite resetar a senha de qualquer usuário do sistema. 
                      Use com cuidado e apenas em situações de emergência.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleForceReset} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email do Usuário *
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={forceForm.email}
                      onChange={handleForceInputChange}
                      required
                      className="field field--lg pl-12"
                      placeholder="Digite o email do usuário"
                    />
                    <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  </div>
                </div>

                <div>
                  <label htmlFor="forceNewPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Nova Senha *
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.forcePassword ? 'text' : 'password'}
                      id="forceNewPassword"
                      name="newPassword"
                      value={forceForm.newPassword}
                      onChange={handleForceInputChange}
                      required
                      minLength={8}
                      className="field field--lg pr-12"
                      placeholder="Digite a nova senha"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('forcePassword')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.forcePassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Mínimo de 8 caracteres</p>
                </div>

                <div className="form-actions">
                  <Button variant="secondary"
                    type="button"
                    onClick={() => navigate('/admin')}>
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}>
                    <Shield className="w-4 h-4" />
                    <span>{loading ? 'Resetando...' : 'Resetar Senha'}</span>
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;
